import { MaybeNull } from '@/shared/types';
import { getSafeRef } from '@/shared/utils/template-refs.ts';
import { Capacitor } from '@capacitor/core';
import anime, { AnimeInstance } from 'animejs';
import {
  onMounted,
  ref,
  Ref,
} from 'vue';
import { App } from '@capacitor/app';

export interface SetVolumeForBackgroundAudioOptions {
  duration: number;
  from?: number;
}

export interface Player {
  play: () => Promise<void>;
  pause: () => void;
  mute: () => void;
  unmute: () => void;
  setSrc: (src: string) => void;
  clearSrc: () => void;
  onEnded: (callback: () => unknown) => void;
  onReadyStateChange: (callback: (readyState: number) => unknown) => void;
  onCanPlay: (callback: () => unknown) => void;
  onCanPlayThrough: (callback: () => unknown) => void;
  onLoadedMetadata: (callback: () => unknown) => void;
  setVolume: (volume: number) => void;
  togglePlay: () => void;
  fadeInVolume: (volume: number, options: SetVolumeForBackgroundAudioOptions) => Promise<void>;
  fadeOutVolume: (options: SetVolumeForBackgroundAudioOptions) => Promise<void>;
  getNativePlayer: () => HTMLAudioElement;
}

export const DEFAULT_VOLUME = 100;

export const DEFAULT_PLAYER_VOLUME_FADE_DURATION = 500;

export const useAudioPlayer = (player$: Ref<MaybeNull<HTMLAudioElement>>): Player => {
  const _volume = ref<number>(100);

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const setSrc = (src: string) => {
    return getSafeRef(player$).src = src;
  };

  const clearSrc = () => {
    return getSafeRef(player$).src = '';
  };

  const play = async () => {
    const appState = await App.getState();

    if (!appState.isActive) {
      return Promise.resolve();
    }

    return await getSafeRef(player$).play();
  };

  const pause = () => {
    return getSafeRef(player$).pause();
  };

  const mute = () => {
    return getSafeRef(player$).muted = true;
  };

  const unmute = () => {
    return getSafeRef(player$).muted = false;
  };

  const getNativePlayer = (): HTMLAudioElement => {
    return getSafeRef(player$);
  };

  const onEnded = (callback: () => unknown) => {
    getSafeRef(player$).onended = () => {
      callback();
    };
  };

  const onReadyStateChange = (callback: (readyState: number) => unknown) => {
    getSafeRef(player$).addEventListener('readystatechange', () => {
      callback(getSafeRef(player$).readyState);
    });
  };

  const onCanPlay = (callback: () => unknown) => {
    getSafeRef(player$).oncanplay = () => {
      callback();
    };
  };

  const onCanPlayThrough = (callback: () => unknown) => {
    getSafeRef(player$).oncanplaythrough = () => {
      callback();
    };
  };

  const onLoadedMetadata = (callback: () => unknown) => {
    getSafeRef(player$).onloadedmetadata = () => {
      callback();
    };
  };

  const setVolume = (value: number) => {
    _volume.value = value;
    getSafeRef(player$).volume = +(value / 100).toFixed(2);
  };

  // FadeIn and FadeOut use the shared animation object
  let volumeAnim: AnimeInstance | null;

  const setVolumeWithFade = (value: number, options?: SetVolumeForBackgroundAudioOptions) => {
    return new Promise<void>((resolve) => {
      if (!getSafeRef(player$)) {
        resolve();
      }

      _volume.value = value;

      if (options?.from !== undefined) {
        getSafeRef(player$).volume = options.from / 100;
      }

      volumeAnim?.pause();
      volumeAnim = null;

      volumeAnim = anime({
        targets: getSafeRef(player$),
        volume: _volume.value / 100,
        round: 100,
        easing: 'linear',
        duration: options?.duration || 2000,
        complete: () => resolve(),
      });

      // on safari there is no way to change volume. We are able to use play pause or mute unmute
      if (isSafari || Capacitor.getPlatform() === 'ios') {
        if (value > 0) {
          play();
        } else {
          pause();
        }
      }
    });
  };

  const fadeInVolume = (volume: number, options?: SetVolumeForBackgroundAudioOptions) => {
    return setVolumeWithFade(volume || DEFAULT_VOLUME, options)
      .then(() => {
        // isVolumeFadedOut.value = false;
      });
  };

  const fadeOutVolume = (options?: SetVolumeForBackgroundAudioOptions) => {
    return setVolumeWithFade(0, options)
      .then(() => {
        // isVolumeFadedOut.value = true;
      });
  };

  const togglePlay = () => {
    if (getSafeRef(player$).paused) {
      getSafeRef(player$).play();
    } else {
      getSafeRef(player$).pause();
    }
  };

  onMounted(() => {
    setVolume(_volume.value);
    mute();

    getSafeRef(player$).onpause = () => {
      console.log('pause');
    };

    getSafeRef(player$).onplay = () => {
      console.log('play');
    };
  });

  return {
    play,
    pause,
    mute,
    unmute,
    setSrc,
    clearSrc,
    onEnded,
    onReadyStateChange,
    onCanPlay,
    onCanPlayThrough,
    onLoadedMetadata,
    setVolume,
    fadeInVolume,
    fadeOutVolume,
    togglePlay,
    getNativePlayer,
  };
};
